import { useState } from 'react';
import { X, TreePine, Layers, FileText, Target, Sparkles, Brain, GitBranch, Leaf, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface ArbrePRIsMEModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const tabs = [
  { id: 'concept', label: 'Le Concept', icon: TreePine },
  { id: 'niveau1', label: 'Niveau 1: PRIsME', icon: Layers },
  { id: 'niveau2', label: 'Niveau 2: Cadres', icon: Target },
  { id: 'niveau3', label: 'Niveau 3: Documents', icon: FileText },
  { id: 'philosophie', label: 'Philosophie', icon: Brain },
];

export default function ArbrePRIsMEModal({ isOpen, onClose }: ArbrePRIsMEModalProps) {
  const [activeTab, setActiveTab] = useState('concept');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['arbre_prisme_concept_last_ai_analysis'] ?? null) : null;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl my-8 max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 text-white p-8 relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              
              <div className="flex items-start gap-4 pr-12">
                <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <TreePine className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="text-sm font-semibold text-white/80">Concept Fondamental</div>
                    {/* Badge AI - Disponible dans AI Assistant */}
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                      className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                      title="Disponible dans AI Assistant"
                    >
                      <Brain className="w-4 h-4 text-purple-200" />
                      <span className="text-xs text-purple-100 font-semibold">AI</span>
                    </motion.div>
                  </div>
                  <h2 className="text-3xl font-bold mb-2">L'Arbre PRIsME</h2>
                  <p className="text-white/90 text-lg mb-2">TRONC / BRANCHE / FEUILLES</p>
                  {lastAnalysisDate && (
                    <div className="flex items-center gap-2 mt-2 text-xs text-white/70">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 bg-gray-50">
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap
                        ${activeTab === tab.id
                          ? 'text-emerald-600 border-b-2 border-emerald-600 bg-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {activeTab === 'concept' && <ConceptTab />}
                  {activeTab === 'niveau1' && <Niveau1Tab />}
                  {activeTab === 'niveau2' && <Niveau2Tab />}
                  {activeTab === 'niveau3' && <Niveau3Tab />}
                  {activeTab === 'philosophie' && <PhilosophieTab />}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Le Concept
function ConceptTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border-l-4 border-emerald-600">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">La Métaphore de l'Arbre</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          PRIsME s'inspire de la structure d'un arbre pour organiser la connaissance, 
          les méthodologies et les livrables. Cette métaphore permet de comprendre 
          la <strong>hiérarchie</strong>, la <strong>cohérence</strong> et la <strong>croissance</strong> 
          de notre écosystème.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white p-5 rounded-lg shadow-sm border-2 border-amber-500">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Layers className="w-6 h-6 text-amber-600" />
              </div>
              <h4 className="font-bold text-gray-900">TRONC</h4>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              La <strong>fondation</strong>, la <strong>base solide</strong> qui supporte tout. 
              C'est l'élément central, stable et permanent.
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg shadow-sm border-2 border-green-500">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <GitBranch className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-bold text-gray-900">BRANCHES</h4>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Les <strong>structures</strong> qui organisent et distribuent. 
              Elles connectent le tronc aux feuilles, créant l'organisation.
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg shadow-sm border-2 border-emerald-500">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Leaf className="w-6 h-6 text-emerald-600" />
              </div>
              <h4 className="font-bold text-gray-900">FEUILLES</h4>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Les <strong>livrables tangibles</strong>, les résultats concrets. 
              C'est ce qui est visible, utilisable et actionnable.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-xl">
        <h4 className="text-xl font-bold text-blue-900 mb-3">🌳 Pourquoi l'Arbre ?</h4>
        <ul className="space-y-2 text-blue-800">
          <li className="flex items-start gap-2">
            <span className="font-bold">•</span>
            <span><strong>Croissance organique</strong> : L'arbre grandit naturellement, comme PRIsME qui s'enrichit continuellement</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">•</span>
            <span><strong>Interdépendance</strong> : Chaque partie dépend des autres, créant un écosystème cohérent</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">•</span>
            <span><strong>Hiérarchie claire</strong> : La structure est intuitive et facile à comprendre</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">•</span>
            <span><strong>Valeur à tous les niveaux</strong> : Du tronc aux feuilles, chaque élément a sa fonction</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// Niveau 1: PRIsME
function Niveau1Tab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Niveau 1 : L'Écosystème PRIsME</h3>
        
        {/* Visual Tree */}
        <div className="bg-white p-8 rounded-xl shadow-sm my-6">
          <div className="flex flex-col items-center">
            {/* TRONC - PRIsME */}
            <div className="bg-gradient-to-b from-indigo-600 to-purple-700 text-white px-8 py-6 rounded-xl shadow-lg mb-4 w-full max-w-md">
              <div className="flex items-center justify-center gap-3">
                <TreePine className="w-8 h-8" />
                <div className="text-center">
                  <div className="text-sm font-semibold opacity-90">TRONC</div>
                  <div className="text-2xl font-bold">PRIsME</div>
                  <div className="text-sm opacity-80 mt-1">Plateforme interne BZ</div>
                </div>
              </div>
            </div>

            {/* BRANCHES - Méthodologies */}
            <div className="flex gap-4 mb-4">
              {['Méthodologie PRIsME', 'Cadres Référentiels', 'Gestion Documentaire', 'Formation'].map((branch, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-gradient-to-b from-green-500 to-emerald-600 text-white px-4 py-3 rounded-lg shadow-md flex items-center gap-2"
                >
                  <GitBranch className="w-4 h-4" />
                  <span className="text-sm font-semibold">{branch}</span>
                </motion.div>
              ))}
            </div>

            {/* FEUILLES - Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 w-full max-w-2xl">
              {['Card 1', 'Card 2', 'Card 3', 'Card 4', 'Card 5', 'Card 6', 'Card 7', 'Card 8'].map((leaf, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 + idx * 0.05 }}
                  className="bg-emerald-100 border-2 border-emerald-300 px-3 py-2 rounded-lg text-center"
                >
                  <Leaf className="w-4 h-4 text-emerald-600 mx-auto mb-1" />
                  <span className="text-xs font-medium text-emerald-800">{leaf}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-indigo-500">
            <h4 className="font-bold text-gray-900 mb-2">🌳 TRONC : PRIsME</h4>
            <p className="text-sm text-gray-700">
              La <strong>plateforme centrale</strong> qui unifie toutes nos méthodologies, 
              cadres et livrables. C'est la fondation sur laquelle tout repose.
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-green-500">
            <h4 className="font-bold text-gray-900 mb-2">🌿 BRANCHES : Méthodologies & Concepts</h4>
            <p className="text-sm text-gray-700">
              Les <strong>structures organisationnelles</strong> : méthodologies, cadres référentiels, 
              gestion documentaire, formation. Elles organisent et distribuent la connaissance.
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-emerald-500">
            <h4 className="font-bold text-gray-900 mb-2">🍃 FEUILLES : Cards & Contenu</h4>
            <p className="text-sm text-gray-700">
              Les <strong>livrables concrets</strong> : chaque card, chaque contenu, chaque outil 
              visible dans PRIsME. C'est ce que les utilisateurs voient et utilisent.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Niveau 2: Cadres
function Niveau2Tab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Niveau 2 : Les Cadres Référentiels</h3>
        
        {/* Visual Tree */}
        <div className="bg-white p-8 rounded-xl shadow-sm my-6">
          <div className="flex flex-col items-center">
            {/* TRONC - Cadre Référentiel */}
            <div className="bg-gradient-to-b from-blue-600 to-cyan-700 text-white px-8 py-6 rounded-xl shadow-lg mb-4 w-full max-w-md">
              <div className="flex items-center justify-center gap-3">
                <Target className="w-8 h-8" />
                <div className="text-center">
                  <div className="text-sm font-semibold opacity-90">TRONC</div>
                  <div className="text-2xl font-bold">Cadre Référentiel</div>
                  <div className="text-sm opacity-80 mt-1">Ex: Cadre BZ, NIST CSF 2.0, ITIL 4, COBIT</div>
                </div>
              </div>
            </div>

            {/* BRANCHES - Catégories */}
            <div className="flex gap-3 mb-4 flex-wrap justify-center">
              {['Catégorie 1', 'Catégorie 2', 'Catégorie 3', 'Catégorie 4'].map((branch, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-gradient-to-b from-teal-500 to-green-600 text-white px-4 py-2 rounded-lg shadow-md text-sm font-semibold"
                >
                  {branch}
                </motion.div>
              ))}
            </div>

            {/* Sous-branches */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 w-full max-w-3xl">
              {['Sous-cat. 1', 'Sous-cat. 2', 'Sous-cat. 3', 'Sous-cat. 4', 'Sous-cat. 5', 'Sous-cat. 6', 'Sous-cat. 7', 'Sous-cat. 8'].map((sub, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 + idx * 0.03 }}
                  className="bg-teal-100 border border-teal-300 px-2 py-1 rounded text-center text-xs font-medium text-teal-800"
                >
                  {sub}
                </motion.div>
              ))}
            </div>

            {/* FEUILLES - Livrables */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 w-full max-w-4xl">
              {Array.from({ length: 12 }, (_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 + i * 0.02 }}
                  className="bg-emerald-100 border-2 border-emerald-300 px-2 py-1 rounded text-center"
                >
                  <Leaf className="w-3 h-3 text-emerald-600 mx-auto" />
                  <span className="text-[10px] font-medium text-emerald-800">Livrable</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-blue-500">
            <h4 className="font-bold text-gray-900 mb-2">🌳 TRONC : Cadre Référentiel</h4>
            <p className="text-sm text-gray-700">
              Le <strong>standard</strong> ou <strong>framework</strong> de référence 
              (Cadre BZ, NIST CSF 2.0, ITIL 4, COBIT 2019, ISO 27001). C'est la base normative.
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-teal-500">
            <h4 className="font-bold text-gray-900 mb-2">🌿 BRANCHES : Catégories & Sous-catégories</h4>
            <p className="text-sm text-gray-700">
              L'<strong>organisation</strong> du cadre : catégories principales, 
              sous-catégories, domaines fonctionnels. Structure logique et hiérarchique.
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-emerald-500">
            <h4 className="font-bold text-gray-900 mb-2">🍃 FEUILLES : Livrables Tangibles</h4>
            <p className="text-sm text-gray-700">
              Les <strong>documents</strong>, <strong>rapports</strong>, <strong>plans</strong> 
              concrets générés selon le cadre. Chaque livrable est actionnable et mesurable.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Niveau 3: Documents
function Niveau3Tab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Niveau 3 : La Gestion Documentaire</h3>
        
        {/* Visual Tree */}
        <div className="bg-white p-8 rounded-xl shadow-sm my-6">
          <div className="flex flex-col items-center">
            {/* TRONC - Type de Document */}
            <div className="bg-gradient-to-b from-purple-600 to-pink-700 text-white px-8 py-6 rounded-xl shadow-lg mb-4 w-full max-w-md">
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-8 h-8" />
                <div className="text-center">
                  <div className="text-sm font-semibold opacity-90">TRONC</div>
                  <div className="text-2xl font-bold">Type de Document</div>
                  <div className="text-sm opacity-80 mt-1">Ex: Plan Directeur, IRP, Politique</div>
                </div>
              </div>
            </div>

            {/* BRANCHES - Template de Formulaire */}
            <div className="flex gap-4 mb-4">
              {['Template Formulaire 1', 'Template Formulaire 2', 'Template Formulaire 3'].map((branch, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-gradient-to-b from-rose-500 to-pink-600 text-white px-4 py-3 rounded-lg shadow-md flex items-center gap-2"
                >
                  <GitBranch className="w-4 h-4" />
                  <span className="text-sm font-semibold">{branch}</span>
                </motion.div>
              ))}
            </div>

            {/* FEUILLES - Documents Créés */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 w-full max-w-3xl">
              {Array.from({ length: 10 }, (_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + i * 0.03 }}
                  className="bg-pink-100 border-2 border-pink-300 px-3 py-2 rounded-lg text-center"
                >
                  <Leaf className="w-4 h-4 text-pink-600 mx-auto mb-1" />
                  <span className="text-xs font-medium text-pink-800">Document</span>
                  <div className="text-[10px] text-pink-600 mt-1">Client {i + 1}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-purple-500">
            <h4 className="font-bold text-gray-900 mb-2">🌳 TRONC : Type de Document</h4>
            <p className="text-sm text-gray-700">
              Le <strong>modèle normé</strong> (Plan Directeur, IRP, Politique, etc.). 
              C'est la structure de base, normée ITIL / COBIT / NIST.
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-rose-500">
            <h4 className="font-bold text-gray-900 mb-2">🌿 BRANCHES : Templates de Formulaire</h4>
            <p className="text-sm text-gray-700">
              Les <strong>formulaires holistiques</strong> qui capturent les besoins. 
              Chaque template permet de créer différents scénarios et contextes.
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-pink-500">
            <h4 className="font-bold text-gray-900 mb-2">🍃 FEUILLES : Documents Créés</h4>
            <p className="text-sm text-gray-700">
              Les <strong>documents finaux</strong> générés pour chaque client. 
              Chaque document est unique, personnalisé et livrable.
            </p>
          </div>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-xl mt-6">
          <h4 className="text-xl font-bold text-amber-900 mb-2">💡 Exemple Concret</h4>
          <p className="text-amber-800 leading-relaxed">
            <strong>Type de Document</strong> : Plan Directeur TI (TRONC) → 
            <strong>Template de Formulaire</strong> : PME vs. Grande Entreprise (BRANCHES) → 
            <strong>Documents Créés</strong> : Plan Directeur pour Client A, B, C... (FEUILLES)
          </p>
        </div>
      </div>
    </div>
  );
}

// Philosophie
function PhilosophieTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">La Philosophie de l'Arbre</h3>
        <p className="text-gray-700 leading-relaxed mb-6">
          L'arbre PRIsME n'est pas qu'une métaphore visuelle. C'est une <strong>philosophie</strong> 
          qui guide notre approche de la connaissance, de l'organisation et de la valeur.
        </p>

        <div className="space-y-6">
          {/* Première ligne : 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-violet-500">
              <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-600" />
                Croissance Organique
              </h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Comme un arbre, PRIsME <strong>grandit naturellement</strong>. Chaque nouveau document, 
                chaque nouvelle méthodologie, chaque nouvelle card enrichit l'écosystème sans casser 
                la structure existante.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-purple-500">
              <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Layers className="w-5 h-5 text-purple-600" />
                Interdépendance
              </h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Chaque partie de l'arbre <strong>dépend des autres</strong>. Le tronc nourrit les branches, 
                les branches portent les feuilles. Aucun élément n'existe isolément - tout est connecté.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-indigo-500">
              <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-600" />
                Valeur à Tous les Niveaux
              </h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                Du <strong>tronc</strong> (fondation) aux <strong>feuilles</strong> (livrables), 
                chaque niveau apporte de la valeur. Le tronc donne la stabilité, les branches 
                l'organisation, les feuilles les résultats.
              </p>
            </div>
          </div>

          {/* Deuxième ligne : 2 cards centrées */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-blue-500">
              <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Brain className="w-5 h-5 text-blue-600" />
                Vision Holistique
              </h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                L'arbre PRIsME permet de <strong>voir l'ensemble</strong> tout en comprenant 
                les détails. On peut zoomer sur une feuille ou prendre du recul pour voir 
                l'arbre complet.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-xl shadow-sm border-l-4 border-amber-500 border-2 border-amber-200">
              <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                Le Tempo
              </h4>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Aller trop vite démontre <strong>peu de richesse</strong> et <strong>dilue le produit</strong>. 
                Ralentir le tempo a une <strong>perception de valeur incroyable</strong>.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                Comprendre ce principe est <strong>essentiel</strong> autant à l'interne qu'à l'externe. 
                Ralentir le processus est <strong>réfléchi</strong> et ça se place au niveau <strong>branche</strong>.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl mt-6 border-l-4 border-emerald-500">
          <h4 className="text-xl font-bold text-emerald-900 mb-3">🌳 L'Esprit de l'Arbre</h4>
          <p className="text-emerald-800 leading-relaxed mb-4">
            L'arbre PRIsME n'est pas statique. Il <strong>évolue</strong>, <strong>s'adapte</strong> 
            et <strong>grandit</strong> avec les besoins. Chaque nouvelle branche, chaque nouvelle 
            feuille enrichit l'écosystème et renforce la valeur globale.
          </p>
          <p className="text-emerald-800 leading-relaxed">
            C'est cette <strong>vision holistique</strong> qui fait de PRIsME plus qu'un outil : 
            c'est un <strong>écosystème vivant</strong> de connaissance et de valeur.
          </p>
        </div>
      </div>
    </div>
  );
}

